// Auth network calls — same-origin (Vite proxies /api/auth → Fastify → yog in
// dev; Fastify proxies in prod), so no host/port is hard-coded.
import { getAccount } from "../lib/account";

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

function authHeader(): Record<string, string> {
  const a = getAccount();
  return a?.token ? { authorization: `Bearer ${a.token}` } : {};
}

interface SessionResponse {
  token: string;
  username: string;
  email?: string | null;
  expiresAt?: number;
}

async function post<T>(path: string, body: unknown, useAuth = false): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: "POST",
      headers: { "content-type": "application/json", ...(useAuth ? authHeader() : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError("Couldn't reach the reArcade server — is it running? (npm run play)", 0);
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError((data as any).error || "Something went wrong. Try again.", res.status, (data as any).code);
  return data as T;
}

export function apiRegister(input: { username: string; password: string; email?: string }) {
  return post<SessionResponse>("/api/auth/register", input);
}

export function apiLogin(input: { username: string; password: string }) {
  return post<SessionResponse>("/api/auth/login", input);
}

export async function apiLogout(): Promise<void> {
  try {
    await post("/api/auth/logout", null, true);
  } catch {
    /* sign out locally even if the server is unreachable */
  }
}

export interface MeResponse {
  revoked?: boolean;
  username?: string;
  email?: string | null;
  createdAt?: number;
  ratings?: unknown[];
}

export async function apiMe(): Promise<MeResponse | null> {
  if (!getAccount()) return null;
  try {
    const res = await fetch("/api/auth/me", { headers: authHeader(), cache: "no-store" });
    if (res.status === 401) return { revoked: true };
    if (!res.ok) return null;
    return (await res.json()) as MeResponse;
  } catch {
    return null; // offline — keep local state
  }
}

export async function apiCheckUsername(username: string): Promise<{ valid: boolean; available: boolean }> {
  try {
    const res = await fetch(`/api/auth/check?username=${encodeURIComponent(username)}`, { cache: "no-store" });
    return await res.json();
  } catch {
    return { valid: true, available: true }; // don't block submit on a flaky check
  }
}
